/* PP (Pending Parcel) user banner — 48h+ pending view + 3-hour password edit window. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Undo2, Unlock } from "lucide-react";
import { PpDailySummary } from "@/components/PpDailySummary";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmt = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export const HandbackTag = ({ r }) => !r.pp_handback_at ? null : (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" onClick={(e) => e.stopPropagation()}
        className="ml-1 inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold text-amber-800 ring-1 ring-amber-300"
        title={`Reason: ${r.pp_handback_note || "—"}`} data-testid={`or-handback-tag-${r.id}`}><Undo2 className="h-2.5 w-2.5" />Handed back</button>
    </PopoverTrigger>
    <PopoverContent className="w-64 p-2 text-xs" data-testid={`or-handback-pop-${r.id}`}>
      <p className="font-semibold text-amber-800">Handed back by {r.pp_handback_by || "PP desk"}</p>
      <p className="text-[10px] text-slate-500">{new Date(r.pp_handback_at).toLocaleString()}</p>
      <p className="mt-1 text-slate-700">{r.pp_handback_note || "—"}</p>
    </PopoverContent>
  </Popover>
);

export const PpUserBar = ({ selected, onChanged }) => {
  const [hb, setHb] = useState(null);
  const [hbDone, setHbDone] = useState(0);
  const [note, setNote] = useState("");
  const [st, setSt] = useState(null);
  const [ask, setAsk] = useState(false);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${API}/order-register/pp/status`, { headers: getAuthHeader() })
    .then(r => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!st?.is_pp_user) return undefined;
    const id = axios.interceptors.response.use(undefined, (err) => {
      if (err.response?.status === 423) { setAsk(true); load(); }
      return Promise.reject(err);
    });
    const t = setInterval(load, 60000);
    return () => { axios.interceptors.response.eject(id); clearInterval(t); };
  }, [st?.is_pp_user, load]);
  if (!st?.is_pp_user) return null;
  const unlock = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/order-register/pp/unlock`, { password: pwd }, { headers: getAuthHeader() });
      toast.success("Editing unlocked for 3 hours — redo your last change if it was blocked"); setAsk(false); setPwd(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Unlock failed"); }
    setBusy(false);
  };
  const lock = async () => {
    await axios.post(`${API}/order-register/pp/lock`, {}, { headers: getAuthHeader() }).catch(() => {});
    load();
  };
  const ids = [...(selected || [])];
  const handback = async (dry) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/order-register/pp/handback`, { ids, dry_run: dry, note }, { headers: getAuthHeader() });
      if (dry) setHb(r.data);
      else {
        toast.success(`Handed back to ${r.data.recipients.map(x => x.name).join(", ")}`);
        setHb(null); setNote(""); setHbDone(n => n + 1); onChanged?.();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Hand back failed"); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900" data-testid="pp-user-bar">
      <span className="rounded bg-amber-200/70 px-1.5 py-0.5 font-semibold" data-testid="pp-view-pill"
        title={`Pending Parcel user — showing only parcels that aren't completed and have an order date on or before ${st.cutoff_date} (${st.pp_hours || 48}h+ old).`}>
        PP view · {st.pp_hours || 48}h+ (≤ {st.cutoff_date})
      </span>
      <PpDailySummary refreshKey={hbDone} />
      {st.unlocked ? (
        <span className="ml-auto inline-flex items-center gap-1.5" data-testid="pp-unlocked">
          <Unlock className="w-3.5 h-3.5 text-emerald-600" /> Unlocked till <b>{fmt(st.unlock_until)}</b>
          <Button size="sm" variant="outline" className="h-5 px-1.5 text-[10px]" onClick={lock} data-testid="pp-lock-btn">Lock now</Button>
        </span>
      ) : (
        <Button size="sm" className="ml-auto h-6 bg-amber-600 px-2 text-[11px] hover:bg-amber-700" onClick={() => setAsk(true)} data-testid="pp-unlock-btn">
          <Lock className="mr-1 w-3.5 h-3.5" /> Unlock editing (3h)
        </Button>
      )}
      {ids.length > 0 && (
        <Button size="sm" variant="outline" className="h-6 border-amber-400 px-2 text-[11px]" disabled={busy}
          onClick={() => handback(true)} data-testid="pp-handback-btn">
          <Undo2 className="mr-1 w-3.5 h-3.5" /> Hand back {ids.length} to team
        </Button>)}
      <Dialog open={!!hb} onOpenChange={(o) => !o && setHb(null)}>
        <DialogContent className="max-w-sm" data-testid="pp-handback-dialog">
          <DialogHeader><DialogTitle className="text-base">Hand back to team</DialogTitle></DialogHeader>
          {hb?.recipients?.length ? (
            <div className="space-y-1 text-xs text-slate-700" data-testid="pp-handback-recipients">
              <p>These rows go back to every team member who handles that service (and the person who first had them), and leave your list:</p>
              {hb.recipients.map(r => <div key={r.email}>• <b>{r.name}</b> — {r.rows} row{r.rows === 1 ? "" : "s"}</div>)}
            </div>) : <p className="text-xs text-rose-700">None of the selected rows can be handed back.</p>}
          {hb?.skipped > 0 && <p className="text-[11px] text-slate-500">{hb.skipped} row(s) skipped — not in your pending window, already invoiced, or no team member holds that service.</p>}
          {hb?.recipients?.length > 0 && (
            <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 200))} rows={2}
              placeholder="Reason (required) — e.g. customer replied, needs your follow-up"
              className="w-full rounded border px-2 py-1 text-xs" data-testid="pp-handback-note" />)}
          <Button onClick={() => handback(false)} disabled={busy || !hb?.recipients?.length || !note.trim()} data-testid="pp-handback-confirm">
            {busy ? "Sending…" : "Hand back"}</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={ask} onOpenChange={setAsk}>
        <DialogContent className="max-w-sm" data-testid="pp-unlock-dialog">
          <DialogHeader><DialogTitle className="text-base">Unlock editing for 3 hours</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-600">Enter your login password. The unlock and every edit you make are recorded in the audit log.</p>
          <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus
            onKeyDown={(e) => e.key === "Enter" && pwd && unlock()} data-testid="pp-unlock-password" placeholder="Password" />
          <Button onClick={unlock} disabled={!pwd || busy} data-testid="pp-unlock-submit">{busy ? "Checking…" : "Unlock"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
